import { ProgramScopedContentRow, ProgramScopedContentRowUpsert, ProgramScopeType } from '../types/domain';
import { supabase } from '../supabaseClient';
import {
  filterProgramScopedContentRowsByTermNumber,
  clearProgramScopedContentRowsCache,
  loadProgramScopedContentRowsCache,
  mergeProgramScopedContentRowsCacheRows,
  saveProgramScopedContentRowsCache,
} from '../utils/programScopedContentRowsCache';
import type { ProgramMutationNotifyOptions } from './programFixedContentService';
import { notifySchoolProgramMutation } from '../utils/schoolDataCacheVersion';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  clearAnnualProgressScopedTermCache,
  loadAnnualProgressScopedTermCache,
  mergeAnnualProgressScopedTermCacheRows,
  saveAnnualProgressScopedTermCache,
} from '../utils/annualProgressScopedTermCache';
import {
  getProgramScopedContentRowsQueryData,
  setProgramScopedContentRowsQueryData,
  setProgramScopedRowsQueryData,
} from '../query/cacheUpdates';
import {
  mergeScopedRowsIntoAnnualGradeTermCaches,
  removeScopedRowIdsFromAnnualGradeTermCaches,
  replaceScopeRowsInAnnualGradeTermCaches,
} from '../utils/programAttachmentRowCacheMerge';
import {
  buildLearningContentScopeVersion,
  learningContentScopeVersionFromHead,
} from '../utils/learningContentCacheVersion';
import {
  copyProgramContentSlidePagesBetweenOwners,
  forkPresentationRefAfterScopedOrFixedRowDuplicate,
  parseSlideCopyFromScopedRowId,
} from './programContentSlideService';
import { forkWorksheetRefAfterScopedOrFixedRowDuplicate } from './programContentWorksheetService';
import { deleteProgramProgressCellsForAnnualKeys } from './programProgressService';
import { normalizeScopedContentRowForStorage } from '../utils/programContentNullNormalize';
import { sortContentRowsByOrder } from '../utils/scheduleContentMatcher';
import {
  applyProgramContentRowsDiff,
  planProgramContentRowsDiff,
  type ProgramContentDiffChange,
} from '../utils/programContentRowsDiff';
import {
  forkSharedProgramContentRefs,
  preserveRemoteProgramContentAttachmentRefs,
  isMissingProgramContentDiffRpcError,
  isStaleBaselineProgramContentDiffRpcError,
} from './programContentRowsDiffSave';

const PROGRAM_SCOPED_CONTENT_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'academic_year',
  'term_number',
  'scope_type',
  'scope_key',
  'order_index',
  'source_annual_cell_key',
  'timetable_text',
  'unit_text',
  'learning_text',
  'learning_note',
  'presentation_ref_id',
  'worksheet_ref_id',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
  'record_member_group_id',
  'bg_color_by_field',
  'created_at',
  'updated_at',
].join(', ');

const PROGRAM_SCOPED_CONTENT_PAGE_SIZE = 1000;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeUuidOrNull(value: unknown): string | null {
  const s = String(value ?? '').trim();
  return s && UUID_RE.test(s) ? s : null;
}

type ProgramScopedDbWriteRow = ProgramScopedContentRowUpsert & {
  id?: string;
  updated_at?: string;
};

function sanitizeScopedRowForDb(row: ProgramScopedDbWriteRow): ProgramScopedDbWriteRow {
  return {
    ...row,
    timetable_text: row.timetable_text ?? '',
    unit_text: row.unit_text ?? '',
    learning_text: row.learning_text ?? '',
    learning_note: row.learning_note ?? '',
    page_text: row.page_text ?? '',
    assistant_page_text: row.assistant_page_text ?? '',
    current_period_text: row.current_period_text ?? '',
    total_period_text: row.total_period_text ?? '',
    material_text: row.material_text ?? '',
    record_cross_curricular_text: row.record_cross_curricular_text ?? '',
    record_memo_text: row.record_memo_text ?? '',
    record_evaluation_text: row.record_evaluation_text ?? '',
    record_class_text: row.record_class_text ?? '',
    presentation_ref_id: normalizeUuidOrNull(row.presentation_ref_id),
    worksheet_ref_id: normalizeUuidOrNull(row.worksheet_ref_id),
    record_member_group_id: normalizeUuidOrNull(row.record_member_group_id),
    bg_color_by_field: row.bg_color_by_field ?? {},
  };
}

function formatSupabaseErrorMessage(error: unknown, context: string): string {
  if (!error || typeof error !== 'object') return `${context}: unknown error`;
  const e = error as { message?: string; details?: string; hint?: string; code?: string };
  const bits = [e.message, e.details, e.hint, e.code].map((v) => String(v ?? '').trim()).filter(Boolean);
  return bits.length ? `${context}: ${bits.join(' | ')}` : `${context}: request failed`;
}

const DELETE_IDS_CHUNK = 200;

/**
 * 화면용 정리: 같은 id 중복만 합치고(최근 수정본), 순서(order_index → created_at → id)로 정렬한다.
 * 셀 키(source_annual_cell_key)로 행을 숨기지 않는다 — 숨기면 "i번째 일정 = i번째 행" 짝이 어긋난다.
 */
function normalizeScopedRowsForDisplay(rows: ProgramScopedContentRow[]): ProgramScopedContentRow[] {
  if (!rows.length) return rows;
  const byId = new Map<string, ProgramScopedContentRow>();
  const withoutId: ProgramScopedContentRow[] = [];
  rows.forEach((row) => {
    const id = (row.id ?? '').trim();
    if (!id) {
      withoutId.push(row);
      return;
    }
    const prev = byId.get(id);
    if (!prev) {
      byId.set(id, row);
      return;
    }
    const prevUpdated = String(prev.updated_at ?? prev.created_at ?? '');
    const nextUpdated = String(row.updated_at ?? row.created_at ?? '');
    if (nextUpdated >= prevUpdated) byId.set(id, row);
  });
  return sortContentRowsByOrder([...withoutId, ...Array.from(byId.values())]);
}

async function fetchProgramScopedContentRowsByQuery(
  baseQuery: {
    range: (
      from: number,
      to: number
    ) => PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>;
  }
): Promise<ProgramScopedContentRow[]> {
  const rows: ProgramScopedContentRow[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await baseQuery.range(offset, offset + PROGRAM_SCOPED_CONTENT_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as unknown as ProgramScopedContentRow[];
    rows.push(...batch);
    if (batch.length < PROGRAM_SCOPED_CONTENT_PAGE_SIZE) break;
    offset += PROGRAM_SCOPED_CONTENT_PAGE_SIZE;
  }
  return rows;
}

/** 스코프별 head — `count:max(updated_at)` (가벼운 2요청). */
export async function fetchProgramScopedContentRowsHead(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string
): Promise<string> {
  if (!workspaceId || !gradeId || !scopeKey) return '0:';
  const { count, error: countError } = await supabase
    .from('program_scoped_content_rows')
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .eq('scope_type', scopeType)
    .eq('scope_key', scopeKey);
  if (countError) throw new Error(countError.message);
  if (!count) return learningContentScopeVersionFromHead(0, '');
  const { data, error } = await supabase
    .from('program_scoped_content_rows')
    .select('updated_at')
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .eq('scope_type', scopeType)
    .eq('scope_key', scopeKey)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return learningContentScopeVersionFromHead(count, maxUpdatedAt);
}

export async function getProgramScopedContentRows(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string,
  options?: { forceRefresh?: boolean }
): Promise<ProgramScopedContentRow[]> {
  if (!workspaceId || !gradeId || !scopeKey) return [];
  if (!options?.forceRefresh) {
    const cached = loadProgramScopedContentRowsCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    );
    if (cached.found && cached.contentVersion) {
      try {
        const headVersion = await fetchProgramScopedContentRowsHead(
          workspaceId,
          academicYear,
          termNumber,
          gradeId,
          scopeType,
          scopeKey
        );
        if (cached.contentVersion === headVersion) {
          const filtered = filterProgramScopedContentRowsByTermNumber(cached.rows, termNumber);
          if (filtered.length > 0 || cached.rows.length === 0) {
            recordDevLocalStorageProgramCacheUse('프로그램 스코프행');
            return filtered;
          }
        }
      } catch {
        /* head 실패 시 풀 조회 */
      }
    } else if (cached.found) {
      const filtered = filterProgramScopedContentRowsByTermNumber(cached.rows, termNumber);
      if (filtered.length > 0 || cached.rows.length === 0) {
        recordDevLocalStorageProgramCacheUse('프로그램 스코프행');
        return filtered;
      }
    }
  }
  const rows = await fetchProgramScopedContentRowsByQuery(
    supabase
    .from('program_scoped_content_rows')
    .select(PROGRAM_SCOPED_CONTENT_SELECT)
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .eq('scope_type', scopeType)
    .eq('scope_key', scopeKey)
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true })
  );
  const normalized = normalizeScopedRowsForDisplay(rows);
  const contentVersion = buildLearningContentScopeVersion(normalized);
  saveProgramScopedContentRowsCache(
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    scopeType,
    scopeKey,
    normalized,
    { contentVersion }
  );
  return normalized;
}

/** 스코프별 고아 행 판별용 — id만 */
export async function listProgramScopedContentRowIds(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string
): Promise<string[]> {
  if (!workspaceId || !gradeId || !scopeKey) return [];
  const ids: string[] = [];
  let offset = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('program_scoped_content_rows')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .eq('term_number', termNumber)
      .eq('grade_id', gradeId)
      .eq('scope_type', scopeType)
      .eq('scope_key', scopeKey)
      .order('order_index', { ascending: true })
      .order('created_at', { ascending: true })
      .range(offset, offset + PROGRAM_SCOPED_CONTENT_PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as Array<{ id: string }>;
    batch.forEach((r) => {
      if (r.id) ids.push(r.id);
    });
    if (batch.length < PROGRAM_SCOPED_CONTENT_PAGE_SIZE) break;
    offset += PROGRAM_SCOPED_CONTENT_PAGE_SIZE;
  }
  return ids;
}

/** 학기·학년 내 모든 편제/프로젝트 스코프 행 head — `count:max(updated_at)` */
export async function fetchProgramScopedContentRowsForGradeTermHead(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Promise<string> {
  if (!workspaceId || !gradeId) return '0:';
  const { data, error, count } = await supabase
    .from('program_scoped_content_rows')
    .select('updated_at', { count: 'exact' })
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (error) throw new Error(error.message);
  const totalCount = count ?? 0;
  const maxUpdatedAt =
    totalCount > 0 && data?.[0]
      ? String((data[0] as { updated_at?: string }).updated_at ?? '')
      : '';
  return learningContentScopeVersionFromHead(totalCount, maxUpdatedAt);
}

/** 학기·학년 내 모든 편제/프로젝트 스코프 행 (기록 모아보기 등 집계용) */
export async function getProgramScopedContentRowsForGradeTerm(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  options?: { forceRefresh?: boolean }
): Promise<ProgramScopedContentRow[]> {
  if (!workspaceId || !gradeId) return [];
  if (!options?.forceRefresh) {
    const cached = loadAnnualProgressScopedTermCache(workspaceId, academicYear, termNumber, gradeId);
    if (cached.found && cached.contentVersion) {
      try {
        const headVersion = await fetchProgramScopedContentRowsForGradeTermHead(
          workspaceId,
          academicYear,
          termNumber,
          gradeId
        );
        if (headVersion === cached.contentVersion) {
          recordDevLocalStorageProgramCacheUse('연간진도·스코프학기·LS(head 일치)');
          return filterProgramScopedContentRowsByTermNumber(cached.rows, termNumber);
        }
      } catch {
        /* head 실패 시 풀 조회 */
      }
    }
  }
  const rows = await fetchProgramScopedContentRowsByQuery(
    supabase
    .from('program_scoped_content_rows')
    .select(PROGRAM_SCOPED_CONTENT_SELECT)
    .eq('workspace_id', workspaceId)
    .eq('academic_year', academicYear)
    .eq('term_number', termNumber)
    .eq('grade_id', gradeId)
    .order('scope_type', { ascending: true })
    .order('scope_key', { ascending: true })
    .order('order_index', { ascending: true })
    .order('created_at', { ascending: true })
  );
  const normalized = normalizeScopedRowsForDisplay(rows);
  saveAnnualProgressScopedTermCache(workspaceId, academicYear, termNumber, gradeId, normalized, {
    contentVersion: buildLearningContentScopeVersion(normalized),
  });
  return normalized;
}

type ScopedRowScopeLike = {
  workspace_id: string;
  grade_id: string;
  academic_year: number;
  term_number: number;
  scope_type: ProgramScopeType;
  scope_key: string;
};

/** 저장 결과 행을 스코프 LS·학년학기 LS·React Query 캐시에 끼워 넣는다(행 단위 병합) */
function mergeSavedScopedRowsIntoClientCaches(rows: readonly ScopedRowScopeLike[], saved: ProgramScopedContentRow[]): void {
  const scopeClearTargets = new Map<
    string,
    {
      workspaceId: string;
      academicYear: number;
      termNumber: 1 | 2;
      gradeId: string;
      scopeType: ProgramScopeType;
      scopeKey: string;
    }
  >();
  [...rows, ...saved].forEach((row) => {
    if (!row.workspace_id || !row.grade_id || !row.scope_key) return;
    const k = `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}|${row.scope_type}|${row.scope_key}`;
    if (scopeClearTargets.has(k)) return;
    scopeClearTargets.set(k, {
      workspaceId: row.workspace_id,
      academicYear: row.academic_year,
      termNumber: row.term_number as 1 | 2,
      gradeId: row.grade_id,
      scopeType: row.scope_type,
      scopeKey: row.scope_key,
    });
  });
  scopeClearTargets.forEach((t) => {
    const scopedSavedRows = saved.filter(
      (row) =>
        row.workspace_id === t.workspaceId &&
        row.academic_year === t.academicYear &&
        row.term_number === t.termNumber &&
        row.grade_id === t.gradeId &&
        row.scope_type === t.scopeType &&
        row.scope_key === t.scopeKey
    );
    const cached = loadProgramScopedContentRowsCache(
      t.workspaceId,
      t.academicYear,
      t.termNumber,
      t.gradeId,
      t.scopeType,
      t.scopeKey
    );
    if (cached.found) {
      const mergedScope = normalizeScopedRowsForDisplay(
        mergeProgramScopedContentRowsCacheRows(
          filterProgramScopedContentRowsByTermNumber(cached.rows, t.termNumber),
          scopedSavedRows
        )
      );
      saveProgramScopedContentRowsCache(
        t.workspaceId,
        t.academicYear,
        t.termNumber,
        t.gradeId,
        t.scopeType,
        t.scopeKey,
        mergedScope
      );
      setProgramScopedContentRowsQueryData(
        t.workspaceId,
        t.academicYear,
        t.termNumber,
        t.gradeId,
        t.scopeType,
        t.scopeKey,
        mergedScope
      );
    } else {
      clearProgramScopedContentRowsCache(
        t.workspaceId,
        t.academicYear,
        t.termNumber,
        t.gradeId,
        t.scopeType,
        t.scopeKey
      );
    }
  });
  const gradeTermClearTargets = new Map<
    string,
    { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string }
  >();
  [...rows, ...saved].forEach((row) => {
    if (!row.workspace_id || !row.grade_id) return;
    const k = `${row.workspace_id}|${row.academic_year}|${row.term_number}|${row.grade_id}`;
    if (gradeTermClearTargets.has(k)) return;
    gradeTermClearTargets.set(k, {
      workspaceId: row.workspace_id,
      academicYear: row.academic_year,
      termNumber: row.term_number as 1 | 2,
      gradeId: row.grade_id,
    });
  });
  gradeTermClearTargets.forEach((t) => {
    const scopedSavedRows = saved.filter(
      (row) =>
        row.workspace_id === t.workspaceId &&
        row.academic_year === t.academicYear &&
        row.term_number === t.termNumber &&
        row.grade_id === t.gradeId
    );
    const cached = loadAnnualProgressScopedTermCache(t.workspaceId, t.academicYear, t.termNumber, t.gradeId);
    if (cached.found) {
      const merged = normalizeScopedRowsForDisplay(
        mergeAnnualProgressScopedTermCacheRows(
          filterProgramScopedContentRowsByTermNumber(cached.rows, t.termNumber),
          scopedSavedRows
        )
      );
      saveAnnualProgressScopedTermCache(
        t.workspaceId,
        t.academicYear,
        t.termNumber,
        t.gradeId,
        merged
      );
      setProgramScopedRowsQueryData(t.workspaceId, t.academicYear, t.termNumber, t.gradeId, merged);
    } else {
      clearAnnualProgressScopedTermCache(t.workspaceId, t.academicYear, t.termNumber, t.gradeId);
      /** LS가 없어도 연간진도 RQ가 있으면 행만 끼워 넣는다 */
      mergeScopedRowsIntoAnnualGradeTermCaches(
        t.workspaceId,
        t.academicYear,
        t.termNumber,
        t.gradeId,
        scopedSavedRows
      );
    }
  });
}

/**
 * 편제·프로젝트 학습내용 행 저장(id 기준). id 있으면 그 행 갱신, 없으면 새 행.
 * 셀 키(source_annual_cell_key)로 기존 행을 찾지 않고, 항상 null로 저장한다.
 * order_index는 호출자가 준 값 그대로(순서 전체를 다시 매길 때는 saveProgramScopedContentRowsDiff 사용).
 */
export async function upsertProgramScopedContentRows(
  rows: ProgramScopedContentRowUpsert[],
  options?: ProgramMutationNotifyOptions
): Promise<ProgramScopedContentRow[]> {
  if (!rows.length) return [];
  const now = new Date().toISOString();
  const toDbRow = (row: ProgramScopedContentRowUpsert) => {
    const { id: _ignoreId, _slidePageCopyFromScopedRowId: _copy, ...rest } = row;
    const normalized = normalizeScopedContentRowForStorage(rest as ProgramScopedContentRowUpsert);
    return {
      ...sanitizeScopedRowForDb(normalized),
      source_annual_cell_key: null,
      updated_at: now,
    };
  };

  const withIdById = new Map<string, ProgramScopedDbWriteRow>();
  rows.forEach((row) => {
    const id = normalizeUuidOrNull(row.id);
    if (!id) return;
    withIdById.set(id, { ...toDbRow(row), id });
  });
  const withId = Array.from(withIdById.values());
  const withoutIdSource = rows.filter((row) => !normalizeUuidOrNull(row.id));
  const withoutId = withoutIdSource.map(toDbRow);

  const saved: ProgramScopedContentRow[] = [];

  if (withId.length) {
    const { data, error } = await supabase
      .from('program_scoped_content_rows')
      .upsert(withId, { onConflict: 'id' })
      .select(PROGRAM_SCOPED_CONTENT_SELECT);
    if (error) {
      throw new Error(formatSupabaseErrorMessage(error, 'program_scoped_content_rows upsert(withId)'));
    }
    saved.push(...(((data ?? []) as unknown) as ProgramScopedContentRow[]));
  }
  if (withoutId.length) {
    const slideCopyFromIds = withoutIdSource.map((row) => parseSlideCopyFromScopedRowId(row));
    const { data, error } = await supabase
      .from('program_scoped_content_rows')
      .insert(withoutId)
      .select(PROGRAM_SCOPED_CONTENT_SELECT);
    if (error) {
      throw new Error(formatSupabaseErrorMessage(error, 'program_scoped_content_rows insert(withoutId)'));
    }
    const inserted = ((data ?? []) as unknown) as ProgramScopedContentRow[];
    saved.push(...inserted);
    const sid = inserted[0]?.workspace_id ?? rows[0]?.workspace_id ?? '';
    if (sid) {
      for (let i = 0; i < inserted.length; i++) {
        const fromId = slideCopyFromIds[i];
        const newId = inserted[i]?.id?.trim();
        if (!fromId || !newId || fromId === newId) continue;
        try {
          await copyProgramContentSlidePagesBetweenOwners(sid, { kind: 'scoped_row', id: fromId }, { kind: 'scoped_row', id: newId });
        } catch {
          /* 슬라이드 복제 실패는 본 저장과 분리 */
        }
        await forkPresentationRefAfterScopedOrFixedRowDuplicate({
          workspaceId: sid,
          table: 'program_scoped_content_rows',
          newRowId: newId,
          learningText: inserted[i]?.learning_text,
        });
        await forkWorksheetRefAfterScopedOrFixedRowDuplicate({
          workspaceId: sid,
          table: 'program_scoped_content_rows',
          newRowId: newId,
          learningText: inserted[i]?.learning_text,
        });
      }
    }
  }

  mergeSavedScopedRowsIntoClientCaches(rows, saved);
  const workspaceId = saved[0]?.workspace_id ?? rows[0]?.workspace_id ?? '';
  if (!options?.deferSchoolVersionNotify && workspaceId) {
    await notifySchoolProgramMutation(workspaceId);
  }
  return saved;
}

const SAVE_SCOPED_ROWS_DIFF_RPC = 'save_program_scoped_content_rows_diff';

function buildScopedRowsDiffRpcRow(
  change: ProgramContentDiffChange<ProgramScopedContentRowUpsert>,
  forceFull: boolean
): Record<string, unknown> {
  const { row, clientKey } = change;
  if (change.positionOnly && !forceFull && row.id) {
    return {
      client_key: clientKey,
      id: row.id,
      position_only: true,
      order_index: row.order_index,
      source_annual_cell_key: row.source_annual_cell_key ?? null,
    };
  }
  const { _slidePageCopyFromScopedRowId: _copy, ...rest } = row;
  const normalized = sanitizeScopedRowForDb(normalizeScopedContentRowForStorage(rest as ProgramScopedContentRowUpsert));
  return { ...normalized, id: row.id ?? null, client_key: clientKey };
}

/**
 * 연간진도용 학년·학기 스코프 행 캐시: 이 스코프 행만 저장 결과로 통째 교체.
 * (예전에는 학기 LS가 없으면 LS만 지우고 끝나 React Query 사본이 옛 행을 계속 보여 줬다)
 */
function replaceScopeRowsInAnnualGradeTermCache(
  params: {
    workspaceId: string;
    academicYear: number;
    termNumber: 1 | 2;
    gradeId: string;
    scopeType: ProgramScopeType;
    scopeKey: string;
  },
  scopeRows: ProgramScopedContentRow[]
): void {
  replaceScopeRowsInAnnualGradeTermCaches(params, normalizeScopedRowsForDisplay(scopeRows));
}

export type SaveProgramScopedContentRowsDiffParams = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  scopeType: ProgramScopeType;
  scopeKey: string;
  /** 마지막으로 불러온 서버 목록(삭제 판별 기준 — 여기 없던 서버 행은 지우지 않는다) */
  baselineRows: readonly ProgramScopedContentRow[];
  /** 화면 표 전체 스냅샷(이 스코프로 정규화된 행) */
  snapshotRows: readonly ProgramScopedContentRowUpsert[];
  /** 동기화 반(alias) fan-out: 대표 스코프가 이미 분리한 첨부 ref를 그대로 공유하므로 다시 복사하지 않는다 */
  skipRefFork?: boolean;
};

const SAVE_SCOPED_ROWS_LAYOUT_RPC = 'save_program_scoped_content_rows_layout';
const ENSURE_SCOPED_ROW_AT_RPC = 'ensure_program_scoped_content_row_at';
const UPDATE_SCOPED_ROW_FIELDS_RPC = 'update_program_scoped_content_row_fields';

/** DB 함수가 아직 없는 환경(마이그레이션 전)에서는 한 번 확인 후 같은 탭에서는 바로 대체 경로로 간다 */
const missingScopedRpc = new Set<string>();

/** 편제·프로젝트 학습내용 스냅샷 정규화: 배열 위치 = order_index, 셀 키는 쓰지 않음(null) */
export function normalizeScopedSnapshotRowsForLayout<T extends ProgramScopedContentRowUpsert>(rows: readonly T[]): T[] {
  return rows.map((row, position) => ({ ...row, order_index: position, source_annual_cell_key: null }));
}

/**
 * 화면 스냅샷 = 저장본(편제·프로젝트). 표 순서 그대로 0..n-1 로 다시 매기고, 바뀐 행 내용·새 행·사라진 행을
 * DB 함수 한 번으로 반영한다. 셀 키(source_annual_cell_key)는 쓰지 않는다(null).
 * 새 DB 함수(save_program_scoped_content_rows_layout)가 없으면 기존 diff 함수 → 클라이언트 순서로 대체한다.
 */
export async function saveProgramScopedContentRowsDiff(
  params: SaveProgramScopedContentRowsDiffParams,
  options?: ProgramMutationNotifyOptions
): Promise<ProgramScopedContentRow[]> {
  const { workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey } = params;
  const baselineRows = sortContentRowsByOrder(
    filterProgramScopedContentRowsByTermNumber([...params.baselineRows], termNumber).filter(
      (row) => row.scope_type === scopeType && row.scope_key === scopeKey
    )
  );
  if (!workspaceId || !gradeId || !scopeKey) return baselineRows;
  const snapshotRows = normalizeScopedSnapshotRowsForLayout(
    params.snapshotRows.map((row) => ({ ...row, scope_type: scopeType, scope_key: scopeKey }))
  );
  const plan = planProgramContentRowsDiff(baselineRows, snapshotRows);
  if (!plan.changes.length && !plan.deleteIds.length) return baselineRows;

  await preserveRemoteProgramContentAttachmentRefs('program_scoped_content_rows', workspaceId, baselineRows, plan);
  if (!params.skipRefFork) await forkSharedProgramContentRefs(workspaceId, baselineRows, plan);

  const refetch = () =>
    getProgramScopedContentRows(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey, {
      forceRefresh: true,
    });
  const scopeArgs = {
    p_workspace_id: workspaceId,
    p_grade_id: gradeId,
    p_academic_year: academicYear,
    p_term_number: termNumber,
    p_scope_type: scopeType,
    p_scope_key: scopeKey,
  };

  let nextRows: ProgramScopedContentRow[] | null = null;

  if (!missingScopedRpc.has(SAVE_SCOPED_ROWS_LAYOUT_RPC)) {
    const changeByClientKey = new Map(plan.changes.map((c) => [c.clientKey, c] as const));
    const callLayout = (forceFull: boolean) =>
      supabase.rpc(SAVE_SCOPED_ROWS_LAYOUT_RPC, {
        ...scopeArgs,
        p_rows: plan.snapshot.map((entry) => {
          const change = changeByClientKey.get(entry.clientKey);
          if (entry.row.id && !forceFull && (!change || change.positionOnly)) {
            return { client_key: entry.clientKey, id: entry.row.id, position_only: true };
          }
          return buildScopedRowsDiffRpcRow(change ?? { clientKey: entry.clientKey, row: entry.row, positionOnly: false }, true);
        }),
        p_delete_ids: plan.deleteIds,
      });
    let { data, error } = await callLayout(false);
    if (error && isStaleBaselineProgramContentDiffRpcError(error)) {
      ({ data, error } = await callLayout(true));
    }
    if (error && isMissingProgramContentDiffRpcError(error)) {
      missingScopedRpc.add(SAVE_SCOPED_ROWS_LAYOUT_RPC);
    } else if (error) {
      throw new Error(formatSupabaseErrorMessage(error, SAVE_SCOPED_ROWS_LAYOUT_RPC));
    } else {
      const payload = (data ?? {}) as { rows?: unknown; count?: number; max_updated_at?: string | null };
      const savedRows = (Array.isArray(payload.rows) ? payload.rows : []).map((raw) => {
        const { client_key: _ck, ...row } = raw as Record<string, unknown>;
        return row as unknown as ProgramScopedContentRow;
      });
      const applied = normalizeScopedRowsForDisplay(savedRows);
      const serverCount = typeof payload.count === 'number' ? payload.count : -1;
      if (serverCount !== applied.length) {
        nextRows = await refetch();
      } else {
        nextRows = applied;
        saveProgramScopedContentRowsCache(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey, applied, {
          contentVersion: learningContentScopeVersionFromHead(serverCount, payload.max_updated_at ?? ''),
        });
      }
    }
  }

  if (!nextRows) {
    /** 새 DB 함수 미적용 환경: 기존 diff 함수(셀 키 null, order = 위치) */
    const callRpc = (forceFull: boolean) =>
      supabase.rpc(SAVE_SCOPED_ROWS_DIFF_RPC, {
        ...scopeArgs,
        p_rows: plan.changes.map((c) => buildScopedRowsDiffRpcRow(c, forceFull)),
        p_delete_ids: plan.deleteIds,
      });
    let { data, error } = await callRpc(false);
    if (error && isStaleBaselineProgramContentDiffRpcError(error)) {
      ({ data, error } = await callRpc(true));
    }
    if (error && isMissingProgramContentDiffRpcError(error)) {
      /** DB 함수가 하나도 없는 환경: 삭제 → id 기준 upsert */
      if (plan.deleteIds.length) {
        await deleteProgramScopedContentRowsByIds(plan.deleteIds, { deferSchoolVersionNotify: true });
      }
      if (plan.changes.length) {
        await upsertProgramScopedContentRows(
          plan.changes.map((c) => c.row),
          { deferSchoolVersionNotify: true }
        );
      }
      nextRows = await refetch();
    } else if (error) {
      throw new Error(formatSupabaseErrorMessage(error, SAVE_SCOPED_ROWS_DIFF_RPC));
    } else {
      const payload = (data ?? {}) as { rows?: unknown; count?: number; max_updated_at?: string | null };
      const savedRows = (Array.isArray(payload.rows) ? payload.rows : []).map((raw) => {
        const { client_key: _ck, ...row } = raw as Record<string, unknown>;
        return row as unknown as ProgramScopedContentRow;
      });
      const applied = normalizeScopedRowsForDisplay(
        applyProgramContentRowsDiff(baselineRows, savedRows, plan.deleteIds)
      );
      const serverCount = typeof payload.count === 'number' ? payload.count : -1;
      if (serverCount !== applied.length) {
        nextRows = await refetch();
      } else {
        nextRows = applied;
        saveProgramScopedContentRowsCache(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey, applied, {
          contentVersion: learningContentScopeVersionFromHead(serverCount, payload.max_updated_at ?? ''),
        });
      }
    }
  }
  setProgramScopedContentRowsQueryData(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey, nextRows);
  replaceScopeRowsInAnnualGradeTermCache(
    { workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey },
    nextRows
  );
  if (!options?.deferSchoolVersionNotify) {
    await notifySchoolProgramMutation(workspaceId);
  }
  return nextRows;
}

/** 학습내용 칸 이름(스코프 행에서 id 기준으로 고칠 수 있는 칸) */
export type ProgramScopedContentRowFieldPatch = Partial<
  Pick<
    ProgramScopedContentRowUpsert,
    | 'timetable_text'
    | 'unit_text'
    | 'learning_text'
    | 'learning_note'
    | 'presentation_ref_id'
    | 'worksheet_ref_id'
    | 'page_text'
    | 'assistant_page_text'
    | 'current_period_text'
    | 'total_period_text'
    | 'material_text'
    | 'record_cross_curricular_text'
    | 'record_memo_text'
    | 'record_evaluation_text'
    | 'record_class_text'
    | 'record_member_group_id'
    | 'bg_color_by_field'
  >
>;

const SCOPED_ROW_PATCH_FIELDS = [
  'timetable_text',
  'unit_text',
  'learning_text',
  'learning_note',
  'presentation_ref_id',
  'worksheet_ref_id',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
  'record_member_group_id',
  'bg_color_by_field',
] as const;

function sanitizeScopedFieldPatch(fields: ProgramScopedContentRowFieldPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  SCOPED_ROW_PATCH_FIELDS.forEach((f) => {
    if (!(f in fields)) return;
    const v = (fields as Record<string, unknown>)[f];
    if (f === 'presentation_ref_id' || f === 'worksheet_ref_id' || f === 'record_member_group_id') {
      out[f] = normalizeUuidOrNull(v);
    } else if (f === 'bg_color_by_field') {
      out[f] = v && typeof v === 'object' ? v : {};
    } else {
      out[f] = v == null ? '' : String(v);
    }
  });
  return out;
}

/**
 * 학습내용 행 하나의 지정한 칸만 id로 고친다(쓰기 1번). 순서·셀 키는 건드리지 않는다.
 * 행이 없으면(다른 기기에서 삭제 등) null.
 */
export async function updateProgramScopedContentRowFieldsById(
  rowId: string,
  fields: ProgramScopedContentRowFieldPatch,
  options?: ProgramMutationNotifyOptions
): Promise<ProgramScopedContentRow | null> {
  const id = normalizeUuidOrNull(rowId);
  if (!id) return null;
  const patch = sanitizeScopedFieldPatch(fields);
  if (!Object.keys(patch).length) return null;
  let saved: ProgramScopedContentRow | null = null;
  let done = false;
  if (!missingScopedRpc.has(UPDATE_SCOPED_ROW_FIELDS_RPC)) {
    const { data, error } = await supabase.rpc(UPDATE_SCOPED_ROW_FIELDS_RPC, { p_id: id, p_fields: patch });
    if (error && isMissingProgramContentDiffRpcError(error)) {
      missingScopedRpc.add(UPDATE_SCOPED_ROW_FIELDS_RPC);
    } else if (error) {
      throw new Error(formatSupabaseErrorMessage(error, UPDATE_SCOPED_ROW_FIELDS_RPC));
    } else {
      saved = (data ?? null) as ProgramScopedContentRow | null;
      done = true;
    }
  }
  if (!done) {
    const { data, error } = await supabase
      .from('program_scoped_content_rows')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select(PROGRAM_SCOPED_CONTENT_SELECT);
    if (error) throw new Error(formatSupabaseErrorMessage(error, 'program_scoped_content_rows update(id)'));
    saved = ((((data ?? []) as unknown) as ProgramScopedContentRow[])[0] ?? null);
  }
  if (!saved) return null;
  mergeSavedScopedRowsIntoClientCaches([saved], [saved]);
  if (!options?.deferSchoolVersionNotify && saved.workspace_id) {
    await notifySchoolProgramMutation(saved.workspace_id);
  }
  return saved;
}

export type ProgramScopedRowScope = {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  scopeType: ProgramScopeType;
  scopeKey: string;
};

/**
 * 스코프 안 position 번째(정렬 기준) 학습내용 행을 돌려준다. 없으면 빈 행을 그 자리까지 채워 만든다.
 * 같은 자리에 두 번 만들지 않는다(DB 함수 안에서 스코프 잠금). DB 함수가 없으면 최신 목록을 읽고 뒤에 이어 붙인다.
 */
export async function ensureProgramScopedContentRowAtPosition(
  scope: ProgramScopedRowScope,
  position: number,
  options?: ProgramMutationNotifyOptions
): Promise<ProgramScopedContentRow | null> {
  const { workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey } = scope;
  if (!workspaceId || !gradeId || !scopeKey || !Number.isInteger(position) || position < 0) return null;
  let target: ProgramScopedContentRow | null = null;
  let created: ProgramScopedContentRow[] = [];
  let done = false;
  if (!missingScopedRpc.has(ENSURE_SCOPED_ROW_AT_RPC)) {
    const { data, error } = await supabase.rpc(ENSURE_SCOPED_ROW_AT_RPC, {
      p_workspace_id: workspaceId,
      p_grade_id: gradeId,
      p_academic_year: academicYear,
      p_term_number: termNumber,
      p_scope_type: scopeType,
      p_scope_key: scopeKey,
      p_position: position,
    });
    if (error && isMissingProgramContentDiffRpcError(error)) {
      missingScopedRpc.add(ENSURE_SCOPED_ROW_AT_RPC);
    } else if (error) {
      throw new Error(formatSupabaseErrorMessage(error, ENSURE_SCOPED_ROW_AT_RPC));
    } else {
      const payload = (data ?? {}) as { row?: unknown; created?: unknown };
      target = (payload.row ?? null) as ProgramScopedContentRow | null;
      created = (Array.isArray(payload.created) ? payload.created : []) as ProgramScopedContentRow[];
      done = true;
    }
  }
  if (!done) {
    const fresh = sortContentRowsByOrder(
      await getProgramScopedContentRows(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey, {
        forceRefresh: true,
      })
    );
    if (fresh[position]) return fresh[position];
    const maxOrder = fresh.reduce((m, row) => Math.max(m, row.order_index ?? 0), -1);
    const base = Math.max(maxOrder + 1, fresh.length);
    const fillers: ProgramScopedContentRowUpsert[] = [];
    for (let k = fresh.length; k <= position; k++) {
      fillers.push(
        normalizeScopedContentRowForStorage({
          workspace_id: workspaceId,
          grade_id: gradeId,
          academic_year: academicYear,
          term_number: termNumber,
          scope_type: scopeType,
          scope_key: scopeKey,
          order_index: base + (k - fresh.length),
          source_annual_cell_key: null,
          timetable_text: '',
          unit_text: '',
          learning_text: '',
          page_text: '',
          assistant_page_text: '',
          current_period_text: '',
          total_period_text: '',
          material_text: '',
          record_cross_curricular_text: '',
          record_memo_text: '',
          record_evaluation_text: '',
          record_class_text: '',
          bg_color_by_field: {},
        })
      );
    }
    const inserted = sortContentRowsByOrder(await upsertProgramScopedContentRows(fillers, { deferSchoolVersionNotify: true }));
    return inserted[inserted.length - 1] ?? null;
  }
  if (created.length) mergeSavedScopedRowsIntoClientCaches(created, created);
  if (created.length && !options?.deferSchoolVersionNotify) {
    await notifySchoolProgramMutation(workspaceId);
  }
  return target;
}

/** 프로젝트 프로그램 등록 시 program_scoped_content_rows가 없으면 최소 1행(또는 일정 슬롯 수)을 만든다. */
export async function ensureProjectProgramScopedContentRows(params: {
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  projectId: string;
  scheduleAnnualCellKeys?: readonly string[];
}): Promise<ProgramScopedContentRow[]> {
  const workspaceId = params.workspaceId.trim();
  const gradeId = params.gradeId.trim();
  const projectId = params.projectId.trim();
  if (!workspaceId || !gradeId || !projectId) return [];

  const existing = await getProgramScopedContentRows(
    workspaceId,
    params.academicYear,
    params.termNumber,
    gradeId,
    'project',
    projectId
  );
  if (existing.length > 0) return existing;

  const scheduleKeys = (params.scheduleAnnualCellKeys ?? [])
    .map((key) => key.trim())
    .filter(Boolean);
  /**
   * 연간 일정 슬롯이 없으면 DB 빈 행을 미리 만들지 않는다.
   * (UI 가상 1행과 레이스하면 저장 시 빈 행이 위에 남고 내용이 한 칸 밀린다)
   */
  if (scheduleKeys.length === 0) return [];
  /** 스코프 잠금 안에서 0..n-1 자리를 채운다 — 두 탭이 동시에 등록해도 같은 자리에 두 번 만들지 않음 */
  const scope = {
    workspaceId,
    academicYear: params.academicYear,
    termNumber: params.termNumber,
    gradeId,
    scopeType: 'project' as const,
    scopeKey: projectId,
  };
  await ensureProgramScopedContentRowAtPosition(scope, scheduleKeys.length - 1);
  return getProgramScopedContentRows(workspaceId, params.academicYear, params.termNumber, gradeId, 'project', projectId, {
    forceRefresh: true,
  });
}

export async function ensureProjectProgramScopedContentRowsForProjects(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  projects: ReadonlyArray<{ id: string; termNumber: 1 | 2 }>
): Promise<void> {
  if (!workspaceId.trim() || !gradeId.trim() || !projects.length) return;
  await Promise.all(
    projects.map((project) =>
      ensureProjectProgramScopedContentRows({
        workspaceId,
        gradeId,
        academicYear,
        termNumber: project.termNumber,
        projectId: project.id,
      })
    )
  );
}

export async function deleteProgramScopedContentRowsByIds(
  ids: string[],
  options?: ProgramMutationNotifyOptions
): Promise<void> {
  const targetIds = ids.map((v) => v.trim()).filter(Boolean);
  if (!targetIds.length) return;
  const { data: rowsToDelete } = await supabase
    .from('program_scoped_content_rows')
    .select('id, workspace_id, academic_year, term_number, grade_id, scope_type, scope_key')
    .in('id', targetIds);
  const { error } = await supabase.from('program_scoped_content_rows').delete().in('id', targetIds);
  if (error) throw new Error(error.message);
  const deletedRows = ((rowsToDelete ?? []) as Array<{
    id: string;
    workspace_id: string;
    academic_year: number;
    term_number: 1 | 2;
    grade_id: string;
    scope_type: ProgramScopeType;
    scope_key: string;
  }>).filter((row) => !!row.id && !!row.workspace_id && !!row.grade_id && !!row.scope_key);
  const grouped = new Map<
    string,
    {
      workspaceId: string;
      academicYear: number;
      termNumber: 1 | 2;
      gradeId: string;
      scopeType: ProgramScopeType;
      scopeKey: string;
      ids: Set<string>;
    }
  >();
  deletedRows.forEach((row) => {
    const key = [
      row.workspace_id,
      row.academic_year,
      row.term_number,
      row.grade_id,
      row.scope_type,
      row.scope_key,
    ].join('|');
    const prev = grouped.get(key);
    if (prev) {
      prev.ids.add(row.id);
      return;
    }
    grouped.set(key, {
      workspaceId: row.workspace_id,
      academicYear: row.academic_year,
      termNumber: row.term_number,
      gradeId: row.grade_id,
      scopeType: row.scope_type,
      scopeKey: row.scope_key,
      ids: new Set([row.id]),
    });
  });
  const workspaceId = deletedRows[0]?.workspace_id ?? '';
  const gradeTermClearTargets = new Map<
    string,
    { workspaceId: string; academicYear: number; termNumber: 1 | 2; gradeId: string }
  >();
  grouped.forEach(({ workspaceId: sid, academicYear, termNumber, gradeId, scopeType, scopeKey, ids }) => {
    const cached = loadProgramScopedContentRowsCache(sid, academicYear, termNumber, gradeId, scopeType, scopeKey);
    if (cached.found) {
      saveProgramScopedContentRowsCache(
        sid,
        academicYear,
        termNumber,
        gradeId,
        scopeType,
        scopeKey,
        cached.rows.filter((row) => !ids.has(row.id))
      );
    } else {
      clearProgramScopedContentRowsCache(sid, academicYear, termNumber, gradeId, scopeType, scopeKey);
    }
    /** 프로그램 표가 보는 스코프 RQ 사본에서도 지운 행을 뺀다 */
    const rqScope = getProgramScopedContentRowsQueryData(sid, academicYear, termNumber, gradeId, scopeType, scopeKey);
    if (rqScope) {
      setProgramScopedContentRowsQueryData(
        sid,
        academicYear,
        termNumber,
        gradeId,
        scopeType,
        scopeKey,
        rqScope.filter((row) => !ids.has(row.id))
      );
    }
    const k = `${sid}|${academicYear}|${termNumber}|${gradeId}`;
    if (!gradeTermClearTargets.has(k)) {
      gradeTermClearTargets.set(k, { workspaceId: sid, academicYear, termNumber, gradeId });
    }
  });
  gradeTermClearTargets.forEach((t) => {
    const deletedIds = new Set(
      deletedRows
        .filter(
          (row) =>
            row.workspace_id === t.workspaceId &&
            row.academic_year === t.academicYear &&
            row.term_number === t.termNumber &&
            row.grade_id === t.gradeId
        )
        .map((row) => row.id)
    );
    /** 예전에는 LS만 고쳐 연간진도표 RQ(staleTime 무한)에 지운 행이 남았다 */
    removeScopedRowIdsFromAnnualGradeTermCaches(t.workspaceId, t.academicYear, t.termNumber, t.gradeId, deletedIds);
  });
  if (!options?.deferSchoolVersionNotify && workspaceId) {
    await notifySchoolProgramMutation(workspaceId);
  }
}

export async function deleteTrackProgramDataForGradeTrackNames(
  workspaceId: string,
  gradeId: string,
  trackNames: string[],
  options?: ProgramMutationNotifyOptions
): Promise<void> {
  const targetNames = new Set(trackNames.map((name) => name.trim()).filter(Boolean));
  if (!workspaceId.trim() || !gradeId.trim() || targetNames.size === 0) return;

  const rows = await fetchProgramScopedContentRowsByQuery(
    supabase
      .from('program_scoped_content_rows')
      .select('id, workspace_id, grade_id, academic_year, term_number, scope_type, scope_key, source_annual_cell_key')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .eq('scope_type', 'track')
      .order('academic_year', { ascending: true })
      .order('term_number', { ascending: true })
      .order('scope_key', { ascending: true })
  );

  const deleteIds: string[] = [];
  const annualKeys: string[] = [];
  rows.forEach((row) => {
    const scopeKey = (row.scope_key ?? '').trim();
    const trackName = scopeKey.includes('|') ? scopeKey.slice(0, scopeKey.indexOf('|')).trim() : scopeKey;
    if (!targetNames.has(trackName)) return;
    if (row.id) deleteIds.push(row.id);
    const annualKey = (row.source_annual_cell_key ?? '').trim();
    if (annualKey) annualKeys.push(annualKey);
  });

  await deleteProgramProgressCellsForAnnualKeys(
    workspaceId,
    gradeId,
    Array.from(new Set(annualKeys)),
    options
  );
  for (let i = 0; i < deleteIds.length; i += DELETE_IDS_CHUNK) {
    await deleteProgramScopedContentRowsByIds(deleteIds.slice(i, i + DELETE_IDS_CHUNK), options);
  }
}

export async function renameTrackProgramDataForGradeTrackName(
  workspaceId: string,
  gradeId: string,
  oldTrackName: string,
  newTrackName: string,
  options?: ProgramMutationNotifyOptions
): Promise<void> {
  const oldName = oldTrackName.trim();
  const newName = newTrackName.trim();
  if (!workspaceId.trim() || !gradeId.trim() || !oldName || !newName || oldName === newName) return;

  const rows = await fetchProgramScopedContentRowsByQuery(
    supabase
      .from('program_scoped_content_rows')
      .select('id, workspace_id, grade_id, academic_year, term_number, scope_type, scope_key, order_index, source_annual_cell_key')
      .eq('workspace_id', workspaceId)
      .eq('grade_id', gradeId)
      .eq('scope_type', 'track')
      .order('academic_year', { ascending: true })
      .order('term_number', { ascending: true })
      .order('scope_key', { ascending: true })
  );

  /**
   * 이름을 바꿀 대상 스코프에 이미 행이 있으면 옮겨 오는 행을 그 뒤 순서로 붙인다(셀 키로 겹침 판단·삭제 안 함).
   */
  const targetMaxOrder = new Map<string, number>();
  rows.forEach((row) => {
    const scopeKey = (row.scope_key ?? '').trim();
    const trackName = scopeKey.includes('|') ? scopeKey.slice(0, scopeKey.indexOf('|')).trim() : scopeKey;
    if (trackName !== newName) return;
    const k = [row.academic_year, row.term_number, scopeKey].join('|');
    const order = typeof row.order_index === 'number' ? row.order_index : 0;
    targetMaxOrder.set(k, Math.max(targetMaxOrder.get(k) ?? -1, order));
  });

  const idsToDelete: string[] = [];
  const updatedRows: Array<{
    id: string;
    academicYear: number;
    termNumber: 1 | 2;
    oldScopeKey: string;
    newScopeKey: string;
  }> = [];

  const moving = rows
    .filter((row) => {
      const scopeKey = (row.scope_key ?? '').trim();
      const sep = scopeKey.indexOf('|');
      const trackName = sep >= 0 ? scopeKey.slice(0, sep).trim() : scopeKey;
      return trackName === oldName && !!row.id;
    })
    .sort((x, y) => (x.order_index ?? 0) - (y.order_index ?? 0));
  const appendedCount = new Map<string, number>();
  for (const row of moving) {
    const scopeKey = (row.scope_key ?? '').trim();
    const sep = scopeKey.indexOf('|');
    const suffix = sep >= 0 ? scopeKey.slice(sep) : '';
    const newScopeKey = `${newName}${suffix}`;
    const targetKey = [row.academic_year, row.term_number, newScopeKey].join('|');
    const patch: { scope_key: string; updated_at: string; order_index?: number } = {
      scope_key: newScopeKey,
      updated_at: new Date().toISOString(),
    };
    if (targetMaxOrder.has(targetKey)) {
      const n = appendedCount.get(targetKey) ?? 0;
      patch.order_index = (targetMaxOrder.get(targetKey) ?? -1) + 1 + n;
      appendedCount.set(targetKey, n + 1);
    }

    const { error } = await supabase.from('program_scoped_content_rows').update(patch).eq('id', row.id);
    if (error) throw new Error(error.message);

    updatedRows.push({
      id: row.id,
      academicYear: row.academic_year,
      termNumber: row.term_number,
      oldScopeKey: scopeKey,
      newScopeKey,
    });
  }

  for (let i = 0; i < idsToDelete.length; i += DELETE_IDS_CHUNK) {
    await deleteProgramScopedContentRowsByIds(idsToDelete.slice(i, i + DELETE_IDS_CHUNK), options);
  }

  const affectedTerms = new Set<string>();
  updatedRows.forEach((row) => {
    clearProgramScopedContentRowsCache(workspaceId, row.academicYear, row.termNumber, gradeId, 'track', row.oldScopeKey);
    clearProgramScopedContentRowsCache(workspaceId, row.academicYear, row.termNumber, gradeId, 'track', row.newScopeKey);
    affectedTerms.add(`${row.academicYear}:${row.termNumber}`);
  });
  affectedTerms.forEach((key) => {
    const [academicYearRaw, termNumberRaw] = key.split(':');
    const academicYear = Number(academicYearRaw);
    const termNumber = Number(termNumberRaw) as 1 | 2;
    if (Number.isInteger(academicYear) && (termNumber === 1 || termNumber === 2)) {
      clearAnnualProgressScopedTermCache(workspaceId, academicYear, termNumber, gradeId);
    }
  });

  if (!options?.deferSchoolVersionNotify && (updatedRows.length > 0 || idsToDelete.length > 0)) {
    await notifySchoolProgramMutation(workspaceId);
  }
}

